import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Table, Button } from "reactstrap";
import {
  ConfirmationModal,
  InformationalModal,
  Loading,
  WarningAlert,
  PaginationControls,
  ItemsPerPageSelector,
  NotificationToast,
  SearchBar,
  SortableHeaderCell
} from "../components";
import useSortableData from "../hooks/useSortableData";
import { useEntity } from "../hooks/useEntity";
import { edgeVersionsApi } from "../api/entitiesApi";
import { updateDevicesApi } from "../api/actionsApi";

export function EdgeVersionsComponent() {
  const { data: versions, isLoading, error } = useEntity(edgeVersionsApi);
  const { items: sortedVersions, requestSort, sortConfig } = useSortableData(versions, {
    key: "uploaded_at",
    direction: "descending"
  });

  const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [versionToUpdate, setVersionToUpdate] = useState(null);

  const [errorMessage, setErrorMessage] = useState({ open: false, title: "", message: "" });

  const filteredVersions = sortedVersions.filter((v) => {
    const lowerTerm = searchTerm.toLowerCase();
    return (
      v.version.toLowerCase().includes(lowerTerm) ||
      v.artifact_name.toLowerCase().includes(lowerTerm)
    );
  });

  const totalPages = Math.ceil(filteredVersions.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentVersions = filteredVersions.slice(indexOfFirst, indexOfLast);

  function toggleDropdown() {
    setDropdownOpen((prev) => !prev);
  }

  function handlePageChange(pageNumber) {
    setCurrentPage(pageNumber);
  }

  function handleDropdownChange(number) {
    setItemsPerPage(number);
    setCurrentPage(1);
  }

  function dismissInformationalModal() {
    setErrorMessage({ open: false, title: "", message: "" });
  }

  async function handleBulkUpdate(version) {
    try {
      await updateDevicesApi.post(version.id);
      setNotification({
        visible: true,
        title: "Devices Updated",
        message: `All devices notified to update to version ${version.version}.`
      });
      setTimeout(() => setNotification({ visible: false, title: "", message: "" }), 2000);
    } catch (err) {
      setErrorMessage({
        open: true,
        title: "Update Failed",
        message:
          "Something went wrong while trying to update the devices."
      });
    }
  }

  if (isLoading) {
    return <Loading />;
  }

  const displayError = error
    ? error.split("\n").map((line, index) => (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      ))
    : "Ooops! No edge versions available.";

  return (
    <>
      {versions.length === 0 && (
        <div className="d-flex flex-column align-items-center">
          <WarningAlert message={displayError} />
          <br />
          <div className="d-flex justify-content-center" />
        </div>
      )}

      <div className="mb-5">
        {versions.length > 0 && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h3 className="m-0">Edge Versions</h3>
              <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>

            {filteredVersions.length > 0 ? (
              <>
                <Table bordered hover responsive size="sm" striped>
                  <thead>
                    <tr>
                      <SortableHeaderCell
                        columnKey="version"
                        label="Version"
                        currentSort={sortConfig}
                        onSort={requestSort}
                      />
                      <SortableHeaderCell
                        columnKey="artifact_name"
                        label="Artifact"
                        currentSort={sortConfig}
                        onSort={requestSort}
                      />
                      <SortableHeaderCell
                        columnKey="uploaded_at"
                        label="Uploaded At"
                        currentSort={sortConfig}
                        onSort={requestSort}
                      />
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVersions.map((v) => (
                      <tr key={v.id}>
                        <td>{v.version}</td>
                        <td>{v.artifact_name}</td>
                        <td>{new Date(v.uploaded_at).toLocaleString()}</td>
                        <td
                          style={{
                            width: "1%",
                            whiteSpace: "nowrap",
                            verticalAlign: "middle"
                          }}
                        >
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => {
                              setVersionToUpdate(v);
                              setConfirmOpen(true);
                            }}
                          >
                            Update All Devices
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                  <ItemsPerPageSelector
                    items={[50, 100]}
                    currentSelection={itemsPerPage}
                    onSelect={handleDropdownChange}
                    isOpen={dropdownOpen}
                    toggle={toggleDropdown}
                  />
                </div>
              </>
            ) : (
              <WarningAlert message="Ooops! No edge versions available" />
            )}
          </>
        )}

        <InformationalModal
          isOpen={errorMessage.open}
          title={errorMessage.title}
          message={errorMessage.message}
          onCancel={dismissInformationalModal}
        />

        <ConfirmationModal
          isOpen={confirmOpen}
          title="Confirm Update"
          message={`Are you sure you want to update all devices to version ${versionToUpdate?.version}?`}
          confirmLabel="Yes, Update"
          cancelLabel="Cancel"
          onConfirm={async () => {
            setConfirmOpen(false);
            await handleBulkUpdate(versionToUpdate);
            setVersionToUpdate(null);
          }}
          onCancel={() => {
            setConfirmOpen(false);
            setVersionToUpdate(null);
          }}
        />

        {notification.visible && (
          <NotificationToast
            icon="exclamation-triangle"
            title={notification.title}
            message={notification.message}
            fade={true}
          />
        )}
      </div>
    </>
  );
}

export default withAuthenticationRequired(EdgeVersionsComponent, {
  onRedirecting: () => <Loading />
});
